import React from 'react';
import {
  List,
  Box,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNotificationPagination } from '../../hooks/useNotificationPagination';
import { useNotificationBulkActions } from '../../hooks/useNotificationBulkActions';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification } from '../../types/notifications';
import NotificationRow from './NotificationRow';

interface NotificationListProps {
  notifications: Notification[];
  onNotificationClick?: (notification: Notification) => void;
  showCheckbox?: boolean;
}

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  onNotificationClick,
  showCheckbox = true,
}) => {
  const { markAsRead, markAsUnread, bulkMarkAsRead, bulkMarkAsUnread } =
    useNotifications();
  const { paginationState, handlePageChange } =
    useNotificationPagination(notifications);

  const { page, pageCount, paginatedNotifications } = paginationState;
  const {
    bulkActionState,
    handleSelectNotification,
    handleBulkMarkAsRead,
    handleBulkMarkAsUnread,
    clearSelection,
  } = useNotificationBulkActions();

  const { selectedNotifications, bulkActionLoading } = bulkActionState;

  const handleMarkAsRead = async (
    notificationId: number,
    event?: React.MouseEvent
  ): Promise<void> => {
    event?.stopPropagation();
    try {
      const notif = paginatedNotifications.find((n) => n.id === notificationId);
      const groupedIds = notif?.related_objects?._groupedIds;
      if (groupedIds?.length) {
        await bulkMarkAsRead(groupedIds);
      } else {
        await markAsRead(notificationId);
      }
    } catch {
      // Error handled silently
    }
  };

  const handleMarkAsUnread = async (
    notificationId: number,
    event?: React.MouseEvent
  ): Promise<void> => {
    event?.stopPropagation();
    try {
      const notif = paginatedNotifications.find((n) => n.id === notificationId);
      const groupedIds = notif?.related_objects?._groupedIds;
      if (groupedIds?.length) {
        await bulkMarkAsUnread(groupedIds);
      } else {
        await markAsUnread(notificationId);
      }
    } catch {
      // Error handled silently
    }
  };

  // Show empty state message if no notifications, but still render pagination for tour
  const showEmptyState = paginatedNotifications.length === 0;

  return (
    <Box>
      {/* Bulk Actions */}
      {showCheckbox && selectedNotifications.length > 0 && (
        <Box mb={2}>
          <Alert
            severity="info"
            action={
              <Box display="flex" gap={1}>
                <IconButton
                  size="small"
                  onClick={handleBulkMarkAsRead}
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? <CircularProgress size={16} /> : '✓'}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleBulkMarkAsUnread}
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? <CircularProgress size={16} /> : '✗'}
                </IconButton>
                <IconButton size="small" onClick={clearSelection}>
                  <CloseIcon />
                </IconButton>
              </Box>
            }
          >
            {selectedNotifications.length} notification
            {selectedNotifications.length !== 1 ? 's' : ''} selected
          </Alert>
        </Box>
      )}

      {/* Empty State Message */}
      {showEmptyState && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={4}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You&apos;re all caught up! New notifications will appear here.
          </Typography>
        </Box>
      )}

      {/* Notification List */}
      {!showEmptyState && (
        <List disablePadding>
          {paginatedNotifications.map((notification, index) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onNotificationClick={onNotificationClick}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsUnread={handleMarkAsUnread}
              onSelect={showCheckbox ? handleSelectNotification : undefined}
              isSelected={
                showCheckbox && selectedNotifications.has(notification.id)
              }
              showCheckbox={showCheckbox}
              showTypeIcon={true}
              showTimestamps={true}
              isDropdown={false}
              data-tour={index === 0 ? 'individual-actions' : undefined}
            />
          ))}
        </List>
      )}

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3} data-tour="pagination">
        <Pagination
          count={Math.max(pageCount, 1)}
          page={page}
          onChange={(_, newPage) => handlePageChange(newPage)}
          color="primary"
          showFirstButton
          showLastButton
          disabled={pageCount <= 1}
        />
      </Box>
    </Box>
  );
};
